import { createMD5, createSHA1 } from 'hash-wasm';
import type { IHasher } from 'hash-wasm/dist/lib/WASMInterface';

const chunkSize = 2 * 1024 * 1024;

export function readFileChunk(chunk: Blob): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const cleanup = () => {
      clearTimeout(timer);
      reader.onload = reader.onerror = reader.onabort = null;
    };
    const timer = setTimeout(() => {
      cleanup();
      reader.abort();
      reject(new Error('File read timed out'));
    }, 30000);
    reader.onload = () => {
      cleanup();
      if (reader.result instanceof ArrayBuffer) resolve(new Uint8Array(reader.result));
      else reject(new Error('Invalid file read result'));
    };
    reader.onerror = () => {
      cleanup();
      reject(reader.error ?? new Error('File read failed'));
    };
    reader.onabort = () => {
      cleanup();
      reject(new Error('File read aborted'));
    };
    try {
      reader.readAsArrayBuffer(chunk);
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
}

async function hashFile(file: Blob, hashers: IHasher[]) {
  for (const hasher of hashers) hasher.init();
  for (let offset = 0; offset < file.size; offset += chunkSize) {
    const bytes = await readFileChunk(file.slice(offset, offset + chunkSize));
    for (const hasher of hashers) hasher.update(bytes);
  }
}

export const getFileHash = async (file: File | Blob, type: 'MD5' | 'SHA1'): Promise<string> => {
  const hasher = type === 'MD5' ? await createMD5() : await createSHA1();
  await hashFile(file, [hasher]);
  return hasher.digest();
};

/** Both digests share one bounded read; each file owns its hasher instances. */
export async function getFileHashes(file: File | Blob) {
  const [md5Hasher, sha1Hasher] = await Promise.all([createMD5(), createSHA1()]);
  await hashFile(file, [md5Hasher, sha1Hasher]);
  return { md5: md5Hasher.digest(), sha1: sha1Hasher.digest() };
}
